// import { commonServices } from "@services/api/Common";
import React, { useEffect, useRef, useState } from "react";
import { commonServices } from "../services/apis";

type Props = {
    label: string;
    value?: string;                         // current URL
    onChange: (url: string) => void;        // receives assetUrl after upload
    disabled?: boolean;
    initialFile?: File | null;
    /**
     * Input accept attribute. Defaults to common images + docs.
     * Override per field as needed.
     */
    accept?: string;
    /** client-side size cap (MB) before any conversion/downscale */
    maxSizeMB?: number;
    forceUploadOnly?: boolean;
    /**
     * Controls behavior:
     * - 'image': only images allowed (HEIC->JPG + downscale apply)
     * - 'file': any file types allowed (no image transforms)
     * - 'auto': allow both; apply image transforms if it is an image
     */
    mode?: "image" | "file" | "auto";
    uploadFn?: (file: File) => Promise<{ assetUrl: string }>;
};

export function UploadField({
    label,
    value,
    onChange,
    disabled,
    accept = "image/*,image/heic,image/heif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/zip",
    maxSizeMB = 100,
    mode = "auto",
    initialFile,
    forceUploadOnly = false,
    uploadFn,
}: Props) {

    const inputRef = useRef<HTMLInputElement | null>(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // brief "Uploaded" state after success
    const [justUploaded, setJustUploaded] = useState(false);
    const uploadedTimerRef = useRef<number | null>(null);

    // display info
    const [lastFileName, setLastFileName] = useState<string | null>(null);
    const [lastFileSize, setLastFileSize] = useState<number | null>(null);

    const handlePick = () => inputRef.current?.click();

    useEffect(() => {
        // reset "Uploaded" flag if parent clears value
        if (!value) setJustUploaded(false);
        return () => {
            if (uploadedTimerRef.current) {
                window.clearTimeout(uploadedTimerRef.current);
            }
        };
    }, [value]);

    useEffect(() => {
        if (initialFile) {
            const fakeEvent = { target: { files: [initialFile] } } as any;
            handleFileChange(fakeEvent);
        }
    }, [initialFile]);

    // --- helpers --------------------------------------------------------------

    const isHeic = (f: File) =>
        /image\/hei(c|f)/i.test(f.type || "") ||
        /\.(heic|heif)$/i.test((f.name || "").toLowerCase());

    const isImage = (f: File) => {
        const type = (f.type || "").toLowerCase();
        if (type) return type.startsWith("image/");
        // fallback for blank MIME from mobile camera
        const name = (f.name || "").toLowerCase();
        return /\.(jpg|jpeg|png|gif|webp|bmp|heic|heif)$/i.test(name);
    };

    function formatBytes(bytes: number) {
        if (bytes === 0) return "0 B";
        const k = 1024;
        const sizes = ["B", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
    }

    // Fallback-safe bitmap loader (only used for images we can decode)
    async function loadBitmap(file: File): Promise<{
        source: CanvasImageSource;
        width: number;
        height: number;
        revoke?: () => void;
    }> {
        if ("createImageBitmap" in window) {
            const bmp = await createImageBitmap(file);
            return {
                source: bmp,
                width: (bmp as ImageBitmap).width,
                height: (bmp as ImageBitmap).height,
            };
        }
        const url = URL.createObjectURL(file);
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const i = new Image();
            i.onload = () => resolve(i);
            i.onerror = reject;
            i.src = url;
        });
        return {
            source: img,
            width: img.naturalWidth,
            height: img.naturalHeight,
            revoke: () => URL.revokeObjectURL(url),
        };
    }

    // Downscale large images (JPEG/PNG/WebP) to speed up uploads
    async function downscaleIfLarge(
        file: File,
        maxDim = 2000,
        quality = 0.85
    ): Promise<File> {
        if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) return file;

        const { source, width, height, revoke } = await loadBitmap(file);
        const largest = Math.max(width, height);
        if (largest <= maxDim) {
            revoke?.();
            return file;
        }

        const scale = maxDim / largest;
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);
        const ctx = canvas.getContext("2d")!;
        ctx.drawImage(source, 0, 0, canvas.width, canvas.height);

        const blob: Blob = await new Promise((resolve) =>
            canvas.toBlob((b) => resolve(b as Blob), "image/jpeg", quality)
        );

        revoke?.();

        return new File(
            [blob],
            (file.name || "image").replace(/\.\w+$/, ".jpg"),
            { type: "image/jpeg" }
        );
    }

    // --- core --------------------------------------------------------------

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const picked = e.target.files?.[0];
        if (!picked) return;

        setError(null);

        // enforce mode rules (block non-images only when mode === "image")
        const pickedIsImage = isImage(picked);
        if (mode === "image" && !pickedIsImage) {
            setError("Please select an image file (jpg, png, webp, heic).");
            e.target.value = "";
            return;
        }

        setUploading(true);

        try {
            let fileToSend: File = picked;

            // Decide whether to apply image transforms:
            // - Always for image/auto modes
            // - For file mode: still convert HEIC so backend doesn't receive HEIC
            const doImageTransforms =
                pickedIsImage && (mode === "image" || mode === "auto" || isHeic(picked));

            if (doImageTransforms) {
                // Convert HEIC/HEIF → JPEG first (if needed)
                if (isHeic(picked)) {
                    try {
                        // robust import shape handling: default or module
                        const mod: any = await import("heic2any");
                        const heic2any = (mod?.default ?? mod) as (opts: any) => Promise<Blob | Blob[]>;

                        const res = await heic2any({
                            blob: picked,
                            toType: "image/jpeg",
                            quality: 0.9,
                        });

                        const jpegBlob = Array.isArray(res) ? res[0] : res;
                        if (!(jpegBlob instanceof Blob)) {
                            throw new Error("Unexpected HEIC conversion output.");
                        }

                        fileToSend = new File(
                            [jpegBlob],
                            (picked.name || "upload").replace(/\.(heic|heif)$/i, ".jpg"),
                            { type: "image/jpeg" }
                        );
                    } catch (convErr) {
                        console.error("HEIC conversion failed:", convErr);
                        setError("We couldn’t convert HEIC to JPEG. Please upload JPG/PNG/WebP instead.");
                        setUploading(false);
                        e.target.value = "";
                        return;
                    }
                }

                // Optional: downscale very large images (after HEIC → JPEG)
                try {
                    fileToSend = await downscaleIfLarge(fileToSend, 2000, 0.85);
                } catch (dsErr) {
                    console.warn("Downscale skipped due to error:", dsErr);
                }
            }

            // Size check AFTER conversions for images (fair for HEIC → JPEG cases)
            const sizeMB = fileToSend.size / (1024 * 1024);
            if (sizeMB > maxSizeMB) {
                setError(`File is too large. Max ${maxSizeMB} MB allowed.`);
                setUploading(false);
                e.target.value = "";
                return;
            }

            // ------------ unique key (folder/path) for backend ------------
            const uid =
                typeof crypto !== "undefined" && "randomUUID" in crypto
                    ? crypto.randomUUID()
                    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

            const d = new Date();
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");

            const safeLabel = (label || "upload")
                .toLowerCase()
                .replace(/[^a-z0-9]+/gi, "-")
                .replace(/^-+|-+$/g, "");

            const uniqueKey = `kyb-uploads/${y}${m}${day}/${safeLabel}/${uid}`;
            // --------------------------------------------------------------

            const uploaded = uploadFn
                ? await uploadFn(fileToSend) // { assetUrl }
                : await commonServices.uploadImage(fileToSend, uniqueKey); // { msg, data: { assetUrl } }

            const assetUrl = (uploaded as any).assetUrl ?? (uploaded as any).data?.assetUrl;
            if (!assetUrl) throw new Error("Upload failed: missing assetUrl.");

            onChange(assetUrl);

            // UI hints
            setLastFileName(fileToSend.name);
            setLastFileSize(fileToSend.size);

            setJustUploaded(true);
            if (uploadedTimerRef.current) window.clearTimeout(uploadedTimerRef.current);
            uploadedTimerRef.current = window.setTimeout(() => setJustUploaded(false), 2000);
        } catch (err: any) {
            setError(err?.message ?? "Failed to upload. Please try again.");
        } finally {
            setUploading(false);
            e.target.value = ""; // allow selecting the same file again
        }
    };




    const showReplace = value && !forceUploadOnly;

    const buttonLabel =
        uploading
            ? "Uploading…"
            : justUploaded
                ? "Uploaded"
                : showReplace
                    ? (mode === "image" ? "Replace Image" : "Replace File")
                    : (mode === "image" ? "Upload Image" : "Upload File");


    return (
        <div className="flex flex-col">
            <label className="text-sm">{label}</label>

            <input
                ref={inputRef}
                type="file"
                accept={accept}
                className="hidden"
                onChange={handleFileChange}
            // capture="environment" // optional: prefer back camera on mobile
            />

            {!forceUploadOnly && <div className="mt-1 flex items-center gap-3">
                <button
                    type="button"
                    className="rounded-md border border-border-subtle px-3 py-2 disabled:opacity-50"
                    onClick={() => {
                        if (!forceUploadOnly || !value) handlePick();
                    }}
                    disabled={disabled || uploading}
                >
                    {buttonLabel}
                </button>
                {justUploaded && !uploading && (
                    <span className="text-[12px] text-[#1E7F45]">✓ saved</span>
                )}
            </div>}

            {/* info row */}
            {(lastFileName || lastFileSize) && (
                <div className="mt-1 text-[10px] text-text-subtle">
                    {lastFileName} {lastFileSize != null && `• ${formatBytes(lastFileSize)}`}
                </div>
            )}

            {error && <div className="mt-1 text-sm text-red-600">{error}</div>}
        </div>
    );
}
export default UploadField;